/**
 * @fileoverview Edit User helpers.
 * @description Pure helpers for the Edit dialog: whether a user's placement can be changed, the
 * form's starting values, and the Update User body built from what changed.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 * @author Anshul Rokde
 */

import type { EditUserFormValues } from '@/features/users/schemas/edit-user-form.schema'
import type { UpdateUserRequest, UserDetail } from '@/features/users/types/users.types'

// Used when a user with no number on file is given one; the form takes Indian numbers only.
const DEFAULT_COUNTRY_CODE = '+91'

/**
 * @description Whether the user's role and reporting manager can be changed: the backend allows it
 * only for an ACTIVE user with one unambiguous primary role.
 * @param {UserDetail} user - The user being edited.
 * @returns {boolean} True when placement is editable.
 */
export function canChangePlacement(user: UserDetail): boolean {
  return user.status === 'ACTIVE' && user.primaryRole !== null
}

/**
 * @description The form's starting values, from the user's current details.
 * @param {UserDetail} user - The user being edited.
 * @returns {EditUserFormValues} The values.
 */
export function toFormValues(user: UserDetail): EditUserFormValues {
  return {
    firstName: user.firstName,
    middleName: user.middleName ?? '',
    lastName: user.lastName,
    mobileNumber: user.mobileNumber ?? '',
    stateId: user.state?.stateId ?? '',
    primaryRoleId: user.primaryRole?.roleId ?? '',
    reportingManagerUserId: user.reportingManager?.userId ?? '',
  }
}

/**
 * @description Builds the Update User body from what changed. A new role is always sent with its
 * manager, since the backend validates the two together.
 * @param {EditUserFormValues} values - The validated values.
 * @param {UserDetail} user - The user as the form was filled from.
 * @returns {UpdateUserRequest} The changed fields only (empty when nothing changed).
 */
export function toUpdateUserRequest(
  values: EditUserFormValues,
  user: UserDetail,
): UpdateUserRequest {
  const initial = toFormValues(user)
  const roleChanged = canChangePlacement(user) && values.primaryRoleId !== initial.primaryRoleId
  const managerChanged =
    canChangePlacement(user) && values.reportingManagerUserId !== initial.reportingManagerUserId

  return {
    ...(values.firstName !== initial.firstName && { firstName: values.firstName }),
    // A blank middle name clears it.
    ...(values.middleName !== initial.middleName && { middleName: values.middleName || null }),
    ...(values.lastName !== initial.lastName && { lastName: values.lastName }),
    ...(values.mobileNumber !== initial.mobileNumber && {
      countryCode: user.countryCode ?? DEFAULT_COUNTRY_CODE,
      mobileNumber: values.mobileNumber,
    }),
    // All India (blank) clears the state.
    ...(values.stateId !== initial.stateId && { stateId: values.stateId || null }),
    ...(roleChanged && { primaryRoleId: values.primaryRoleId }),
    ...((roleChanged || managerChanged) && {
      reportingManagerUserId: values.reportingManagerUserId,
    }),
  }
}
